import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Inbox, CheckCircle2, RotateCcw, ChevronDown, ChevronUp } from "lucide-react";
import AdminLayout from "../components/AdminLayout";
import NotesPanel from "../components/NotesPanel";
import { adminApi } from "../lib/adminApi";

const STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "new", label: "New" },
  { value: "handled", label: "Handled" },
];
const VARIANT_OPTIONS = [
  { value: "all", label: "All types" },
  { value: "contact", label: "Enquiry" },
  { value: "consultation", label: "Consultation" },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ total: 0, new: 0, handled: 0 });
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [variant, setVariant] = useState("all");
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const fetchEnquiries = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.get("/admin/enquiries", { params: { search, status, variant } });
      setItems(res.data.items);
      setCounts(res.data.counts);
    } catch (err) {
      if (err.response?.status === 401) navigate("/admin");
    } finally {
      setLoading(false);
    }
  }, [search, status, variant, navigate]);

  useEffect(() => {
    const t = setTimeout(fetchEnquiries, search ? 350 : 0);
    return () => clearTimeout(t);
  }, [fetchEnquiries, search]);

  const toggleHandled = async (enquiry) => {
    const res = await adminApi.patch(`/admin/enquiries/${enquiry.id}`, { handled: !enquiry.handled });
    setItems((prev) => prev.map((it) => (it.id === enquiry.id ? res.data : it)));
    setCounts((c) => ({
      ...c,
      new: c.new + (res.data.handled ? -1 : 1),
      handled: c.handled + (res.data.handled ? 1 : -1),
    }));
  };

  return (
    <AdminLayout title="Enquiries Dashboard">
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Total", value: counts.total, testid: "admin-count-total" },
          { label: "New", value: counts.new, testid: "admin-count-new" },
          { label: "Handled", value: counts.handled, testid: "admin-count-handled" },
        ].map((c) => (
          <div key={c.label} className="border border-border bg-white p-5" data-testid={c.testid}>
            <p className="text-xs font-semibold uppercase tracking-wider text-brand-grey">{c.label}</p>
            <p className="mt-1 text-3xl font-semibold text-brand-graphite">{c.value}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-grey" aria-hidden="true" />
          <input
            type="search"
            className="field-input pl-10"
            placeholder="Search by name, email, company or service…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="admin-search-input"
          />
        </div>
        <select className="field-input sm:w-44" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="admin-status-filter">
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <select className="field-input sm:w-44" value={variant} onChange={(e) => setVariant(e.target.value)} data-testid="admin-variant-filter">
          {VARIANT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      <div className="mt-6 space-y-3" data-testid="admin-enquiry-list">
        {loading ? (
          <p className="py-12 text-center text-sm text-brand-grey">Loading enquiries…</p>
        ) : items.length === 0 ? (
          <div className="border border-border bg-white p-12 text-center" data-testid="admin-empty-state">
            <Inbox className="mx-auto h-8 w-8 text-brand-grey" aria-hidden="true" />
            <p className="mt-3 text-sm text-brand-grey">No enquiries match your filters.</p>
          </div>
        ) : (
          items.map((e) => (
            <div key={e.id} className="border border-border bg-white" data-testid={`admin-enquiry-${e.id}`}>
              <div className="flex flex-wrap items-center gap-3 p-4 sm:p-5">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-semibold text-brand-graphite">{e.fullName}</p>
                    <span className="bg-brand-surface px-2 py-0.5 text-xs font-medium text-brand-grey">
                      {e.variant === "consultation" ? "Consultation" : "Enquiry"}
                    </span>
                    <span
                      className={`px-2 py-0.5 text-xs font-semibold ${
                        e.handled ? "bg-neutral-100 text-neutral-500" : "bg-brand-red text-white"
                      }`}
                      data-testid={`admin-enquiry-status-${e.id}`}
                    >
                      {e.handled ? "Handled" : "New"}
                    </span>
                  </div>
                  <p className="mt-1 truncate text-sm text-brand-grey">
                    {e.company} · {e.email} · {e.countryCode} {e.phone} · {e.service}
                  </p>
                  <p className="mt-0.5 text-xs text-neutral-400">
                    {new Date(e.createdAt).toLocaleString()} · prefers {e.contactMethod}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setExpanded(expanded === e.id ? null : e.id)}
                    className="btn-secondary flex items-center gap-1.5 px-3 py-2 text-xs"
                    data-testid={`admin-enquiry-toggle-details-${e.id}`}
                  >
                    Details {expanded === e.id ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleHandled(e)}
                    className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold transition-colors ${
                      e.handled
                        ? "border border-border text-brand-grey hover:text-brand-graphite"
                        : "bg-brand-red text-white hover:bg-red-700"
                    }`}
                    data-testid={`admin-enquiry-handled-button-${e.id}`}
                  >
                    {e.handled ? (
                      <>
                        <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" /> Mark as New
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" /> Mark Handled
                      </>
                    )}
                  </button>
                </div>
              </div>
              {expanded === e.id && (
                <div className="border-t border-border bg-brand-surface p-4 sm:p-5" data-testid={`admin-enquiry-details-${e.id}`}>
                  <p className="text-xs font-semibold uppercase tracking-wider text-brand-grey">Project Details</p>
                  <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-brand-graphite">{e.details}</p>
                  <NotesPanel parentType="enquiry" parentId={e.id} />
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </AdminLayout>
  );
}
